import React, { useEffect, useReducer } from 'react';
import { EventListPageContext } from './EventListPageContext';
import moment from 'moment';
import { useTranslation } from 'next-i18next';
import { basePageWrap } from '../BasePage';
import s from './EventListPage.module.scss';
import DatePicker from '../../components/DatePicker/DatePicker';
import EventFilters from '../../components/EventFilters/EventFilters';
import CardEventList from '../../components/CardEventList/CardEventList';
import Pagination from '../../components/Pagination/Pagination';
import EventSubmission from '../../components/EventSubmission/EventSubmission';
import StreamField from '../../components/StreamField/StreamField';
import EventSubjects from '../../components/EventCategories/EventSubjects';
import GradientBlurb from '../../components/GradientBlurb';
import { keysToCamelFromSnake } from '../../utils/caseconverters';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';

function _filterNull(obj) {
  const ret = {};
  for (const key in obj) {
    if (obj[key] !== null) {
      ret[key] = obj[key];
    }
  }

  return ret;
}

function updateGetParams(reqArgs) {
  const nextUrlParams = new URLSearchParams('');
  if (reqArgs.dateFrom !== undefined) {
    if (typeof reqArgs.dateFrom === 'string') {
      nextUrlParams.set('date_from', reqArgs.dateFrom);
    } else {
      nextUrlParams.set(
        'date_from',
        reqArgs.dateFrom.toISOString().substr(0, 10)
      );
    }
  }
  if (reqArgs.dateTo !== undefined) {
    if (typeof reqArgs.dateTo === 'string') {
      nextUrlParams.set('date_to', reqArgs.dateTo);
    } else {
      nextUrlParams.set('date_to', reqArgs.dateTo.toISOString().substr(0, 10));
    }
  }

  if (reqArgs.oneTime !== undefined) {
    nextUrlParams.set('one_time', reqArgs.oneTime);
  }

  if (reqArgs.freeEventsOnly !== undefined) {
    nextUrlParams.set('free_events_only', reqArgs.freeEventsOnly);
  }

  if (reqArgs.digitalEventsOnly !== undefined) {
    nextUrlParams.set('digital_events_only', reqArgs.digitalEventsOnly);
  }

  if (reqArgs.impactDaysOnly !== undefined) {
    nextUrlParams.set('impact_days_only', reqArgs.impactDaysOnly);
  }

  if (reqArgs.categories) {
    reqArgs.categories.forEach((category) => {
      nextUrlParams.append('categories', category);
    });
  }

  if (Array.isArray(reqArgs.languages)) {
    reqArgs.languages.forEach((lang) => {
      nextUrlParams.append('languages', lang);
    });
  }

  if (Array.isArray(reqArgs.eventTypes)) {
    reqArgs.eventTypes.forEach((type) => {
      nextUrlParams.append('event_types', type);
    });
  }

  if (reqArgs.page !== undefined && reqArgs.page !== 1) {
    nextUrlParams.set('page', reqArgs.page);
  }

  window.history.replaceState({}, '', `?${nextUrlParams.toString()}`);
}

export const ACTION_TYPES = {
  REQUEST_STARTED: 'REQUEST_STARTED',
  REQUEST_FAILED: 'REQUEST_FAILED',
  CATEGORIES_RECEIVED: 'CATEGORIES_RECEIVED',
  EVENTS_RECEIVED: 'EVENTS_RECEIVED',
  CHANGE_DATES: 'CHANGE_DATES',
  LOAD_MORE: 'LOAD_MORE',
  TOGGLE_ONE_TIME: 'TOGGLE_ONE_TIME',
  TOGGLE_FREE: 'TOGGLE_FREE',
  TOGGLE_DIGITAL: 'TOGGLE_DIGITAL',
  TOGGLE_IMPACT_DAYS: 'TOGGLE_IMPACT_DAYS',
  CATEGORY_SELECTED: 'CATEGORY_SELECTED',
  LANGUAGE_SELECTED: 'LANGUAGE_SELECTED',
  EVENT_TYPES_SELECTED: 'EVENT_TYPES_SELECTED',
  CLEAR_CATEGORIES: 'CLEAR_CATEGORIES',
  SHOW_SEARCH_RESULTS: 'SHOW_SEARCH_RESULTS',
  SET_REQUEST_ARGUMENTS: 'SET_REQUEST_ARGUMENTS',
  CHANGE_PAGE: 'CHANGE_PAGE',
  RESET: 'RESET',
  COMPLETE_INITIAL_LOAD: 'COMPLETE_INITIAL_LOAD',
  CLEAR_ALL_FILTERS: 'CLEAR_ALL_FILTERS',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.COMPLETE_INITIAL_LOAD:
      return {
        ...state,
        isInitialLoad: false,
      };
    case ACTION_TYPES.RESET:
      return {
        ...state,
        activeCategories: [],
        categories: [],
        selectedCategories: [],
        hasMoreContent: null,
        oneTime: null,
        dateFrom: null,
        dateTo: null,
        page: 1,
        freeEventsOnly: null,
        digitalEventsOnly: null,
        impactDaysOnly: null,
      };

    case ACTION_TYPES.REQUEST_STARTED:
      return { ...state, loading: true };

    case ACTION_TYPES.REQUEST_FAILED:
      return { ...state, loading: false, error: true };

    case ACTION_TYPES.CATEGORIES_RECEIVED:
      const { categories } = action;

      return {
        ...state,
        categories,
      };

    case ACTION_TYPES.CLEAR_ALL_FILTERS:
      return {
        ...state,
        selectedEventTypes: [],
        selectedLanguages: [],
        freeEventsOnly: null,
        digitalEventsOnly: null,
        impactDaysOnly: null,
        page: 1,
      };

    case ACTION_TYPES.EVENTS_RECEIVED:
      const { isFirstPage, hasMoreContent, count } = action;
      const events = isFirstPage ? action.events : [...action.events];
      const nextPage = isFirstPage ? 2 : state.nextPage + 1;
      return {
        ...state,
        events,
        hasMoreContent,
        nextPage,
        loading: false,
        error: false,
        showSearchResults: true,
        count,
        pagination: {
          next: action.next,
          previous: action.previous,
          totalPages: action.totalPages,
          currentPage: action.currentPage,
        },
      };

    case ACTION_TYPES.CHANGE_DATES:
      const { startDate, endDate } = action;
      return {
        ...state,
        dateFrom: startDate,
        dateTo: endDate ? endDate : startDate,
        page: 1,
        events: [],
      };

    case ACTION_TYPES.CHANGE_PAGE:
      return {
        ...state,
        page: action.page,
      };

    case ACTION_TYPES.TOGGLE_FREE:
      if (!state.freeEventsOnly) {
        // dataLayerPush({
        //     event: 'filter',
        //     filtercategory: 'Free events',
        // });
      }

      return {
        ...state,
        freeEventsOnly: !state.freeEventsOnly,
        page: 1,
      };

    case ACTION_TYPES.TOGGLE_DIGITAL:
      if (!state.digitalEventsOnly) {
        // dataLayerPush({
        //     event: 'filter',
        //     filtercategory: 'Digital events',
        // });
      }
      return {
        ...state,
        digitalEventsOnly: !state.digitalEventsOnly,
        page: 1,
      };

    case ACTION_TYPES.TOGGLE_IMPACT_DAYS:
      return {
        ...state,
        impactDaysOnly: !state.impactDaysOnly,
        page: 1,
      };

    case ACTION_TYPES.LANGUAGE_SELECTED:
      const { languages } = action;

      return {
        ...state,
        selectedLanguages: languages,
        page: 1,
      };

    case ACTION_TYPES.EVENT_TYPES_SELECTED:
      const { eventTypes } = action;

      return {
        ...state,
        selectedEventTypes: eventTypes,
        page: 1,
      };

    case ACTION_TYPES.CATEGORY_SELECTED:
      const { category, excludeCategory } = action;

      let sCategories = [
        ...state.selectedCategories.filter((x) => x !== excludeCategory),
      ];

      if (!excludeCategory) {
        sCategories.push(category.slug);
        // dataLayerPush({
        //     event: 'filter',
        //     filtercategory: category?.title,
        // });
      }

      return {
        ...state,
        selectedCategories: sCategories,
        page: 1,
      };

    case ACTION_TYPES.CLEAR_CATEGORIES:
      return {
        ...state,
        selectedCategories: [],
        page: 1,
      };

    case ACTION_TYPES.SHOW_SEARCH_RESULTS:
      const { loading, showSearchResults } = action;
      return {
        ...state,
        showSearchResults: showSearchResults,
        loading,
      };

    case ACTION_TYPES.SET_REQUEST_ARGUMENTS:
      const { requestArguments } = action;
      return {
        ...state,
        requestArguments: requestArguments,
      };

    default:
      throw new Error('Unknown action: ' + action.type);
  }
};

const EventListPage = ({
  eventTypes = [],
  subjects = [],
  events = [],
  selectedFilters = {},
  label,
  titleTop,
  titleBottom,
  eventSubmissionUrl = '',
  contentBlocks,
  languages,
}) => {
  const { i18n, t } = useTranslation();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  let search = typeof window !== 'undefined' ? window.location.search : '';
  const urlParams = new URLSearchParams(search);
  const normalizeArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

  const initialState = {
    activeCategories: subjects,
    categories: subjects,
    selectedCategories: selectedFilters?.categories
      ? selectedFilters.categories
      : urlParams.getAll('categories'),
    hasMoreContent: null,
    oneTime: urlParams.get('one_time'),
    dateFrom: selectedFilters.dateFrom
      ? moment(selectedFilters.dateFrom)
      : urlParams.get('date_from')
        ? new Date(urlParams.get('date_from'))
        : null,
    dateTo: selectedFilters.dateTo
      ? moment(selectedFilters.dateTo)
      : urlParams.get('date_to')
        ? new Date(urlParams.get('date_to'))
        : null,
    page: urlParams.get('page') || 1,
    freeEventsOnly: selectedFilters.freeEventsOnly
      ? true
      : urlParams.get('free_events_only'),
    digitalEventsOnly: selectedFilters.digitalEventsOnly
      ? true
      : urlParams.get('digital_events_only'),
    impactDaysOnly: selectedFilters.impactDaysOnly
      ? true
      : urlParams.get('impact_days_only'),
    nextPage: 2,
    loading: false,
    error: false,
    events: events?.items || [],
    showSearchResults: false,
    languages: languages,
    selectedLanguages: selectedFilters?.languages
      ? selectedFilters.languages
      : urlParams.getAll('languages'),
    eventTypes: eventTypes,

    selectedEventTypes: normalizeArray(
      selectedFilters?.eventTypes ?? urlParams.getAll('event_types')
    ),

    // showSearchResults: isSsr
    //     ? true
    //     : !!selectedFilters.categories ||
    //       !!selectedFilters.dateFrom ||
    //       !!selectedFilters.dateTo ||
    //       urlParams.get('categories') ||
    //       urlParams.get('date_from') ||
    //       urlParams.get('date_to') ||
    //       urlParams.get('one_time'),
    requestArguments: null,
    pagination: events.pagination,
    count: events.count || null,
    isInitialLoad: true,
  };

  useEffect(() => {
    dispatch({
      type: ACTION_TYPES.COMPLETE_INITIAL_LOAD,
    });
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    console.log('EVENT TYPES', state.selectedEventTypes);
    console.log('LANGUAGES', state.selectedLanguages);
  }, [state.selectedEventTypes, state.selectedLanguages]);

  const fetchData = () => {
    if (state.loading) {
      abortSingularEventsRequests();
    }

    dispatch({ type: ACTION_TYPES.REQUEST_STARTED });

    const reqArgs = _filterNull({
      page: state.page,
      categories: state.selectedCategories.length
        ? state.selectedCategories
        : null,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      freeEventsOnly: state.freeEventsOnly ? state.freeEventsOnly : null,
      digitalEventsOnly: state.digitalEventsOnly
        ? state.digitalEventsOnly
        : null,
      impactDaysOnly: state.impactDaysOnly ? state.impactDaysOnly : null,
      oneTime: state.oneTime ? state.oneTime : null,
      languages: state.selectedLanguages.length
        ? state.selectedLanguages
        : null,
      eventTypes: state.selectedEventTypes.length
        ? state.selectedEventTypes
        : null,
    });

    dispatch({
      type: ACTION_TYPES.SET_REQUEST_ARGUMENTS,
      requestArguments: reqArgs,
    });

    const hasFilters =
      reqArgs.categories ||
      reqArgs.dateFrom ||
      reqArgs.dateTo ||
      reqArgs.freeEventsOnly ||
      reqArgs.digitalEventsOnly ||
      reqArgs.impactDaysOnly ||
      reqArgs.languages ||
      reqArgs.eventTypes;

    if (!hasFilters) {
      dispatch({
        type: ACTION_TYPES.SHOW_SEARCH_RESULTS,
        showSearchResults: false,
      });
      return;
    }

    updateGetParams(reqArgs);

    const searchParams = new URLSearchParams();

    function camelToSnake(str) {
      return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    }

    Object.entries(reqArgs).forEach(([key, value]) => {
      const snakeKey = camelToSnake(key);
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(snakeKey, v));
      } else {
        searchParams.append(snakeKey, value);
      }
    });

    fetch(`/wt/api/v1/events/?${searchParams.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then((data) => {
        data = keysToCamelFromSnake(data);
        dispatch({
          type: ACTION_TYPES.EVENTS_RECEIVED,
          events: data.results,
          hasMoreContent: !!data.next,
          isFirstPage: !data.previous,
          totalPages: data.totalPages,
          currentPage: data.currentPage,
          next: data.next,
          showSearchResults: true,
          previous: data.previous,
          count: data.count,
          loading: false,
        });
        dispatch({
          type: ACTION_TYPES.CATEGORIES_RECEIVED,
          categories: data.meta.categories,
        });
      })
      .catch((e) => {
        if (e.name === 'AbortError') {
          return;
        }

        dispatch({ type: ACTION_TYPES.REQUEST_FAILED });
        console.error(e);
      });
  };

  useEffect(fetchData, [
    state.oneTime,
    state.page,
    state.dateFrom,
    state.selectedCategories,
    state.selectedLanguages.length,
    state.dateTo,
    state.freeEventsOnly,
    state.digitalEventsOnly,
    state.impactDaysOnly,
    state.selectedEventTypes.length,
  ]);

  const classes = classNames(s.Root, {
    [s.Visible]: inView,
  });

  return (
    <div className={classes} ref={ref}>
      <GradientBlurb modifiers={['EventListPage']} />
      <GradientBlurb modifiers={['EventListPage--Left']} />
      <EventListPageContext.Provider value={{ state, dispatch }}>
        <div className={s.Container}>
          <div className={s.Hero}>
            <div className={s.TopWrapper}>
              <div className={s.TitleContainer}>
                <div className={s.Label}>{label}</div>
                <h1 className={s.Title}>
                  <span className={s.TitleTop}>{titleTop}</span>
                  <span className={s.TitleBottom}>{titleBottom}</span>
                </h1>
              </div>
              <div className={s.DatePicker}>
                <DatePicker />
              </div>
            </div>
            <div className={s.BottomWrapper}>
              <EventSubjects subjects={subjects} />
              <div className={s.Divider} />
              <EventFilters eventTypes={eventTypes} />
            </div>
          </div>
        </div>
        <div className={s.EventList} id="top-of-search">
          {!!state.showSearchResults && state.count === 0 && (
            <div className={s.NoHitsWrapper}>
              <h2 className={s.NoHitsTitle}>
                {t('EventListPage.noHitsTitle')}
              </h2>
              <p className={s.NoHitsText}>{t('EventListPage.noHitsText')}</p>
            </div>
          )}
          {state.showSearchResults && !state.loading && (
            <>
              <CardEventList
                items={state.events}
                isSearchResult={true}
                modifiers={['EventListPage']}
                title={
                  !!state.count &&
                  t('CardEventList.eventCount', {
                    count: state.count,
                  })
                }
              />
              {!!state.pagination && state.pagination.totalPages > 1 && (
                <Pagination />
              )}
            </>
          )}

          {state.loading && (
            <CardEventList
              items={[]}
              isSearchResult={true}
              isLoading={true}
              title={''}
              image={''}
            />
          )}

          {!state.showSearchResults && !state.loading && (
            <div className={s.StreamField}>
              <StreamField
                items={contentBlocks}
                fromEventList={true}
                modifiers={['EventListPage']}
              />
            </div>
          )}

          {!!eventSubmissionUrl && (
            <div className={s.EventSubmission}>
              <EventSubmission
                title={t('EventSubmission.title')}
                text={t('EventSubmission.text')}
                href={eventSubmissionUrl}
                buttonText={t('EventSubmission.buttonText')}
              />
            </div>
          )}
        </div>
        <span className={s.BlobTop} />
        <span className={s.BlobRight} />
        <span className={s.BlobLeft} />
      </EventListPageContext.Provider>
    </div>
  );
};

export default basePageWrap(EventListPage);
